// ---------------------------------------------------------------------------
// PlanogramPriceUtils
//
// GH #534: the Price field only accepted a dot as the decimal separator —
// typing a comma (the standard separator in Polish, and most of continental
// Europe) either did nothing (native <input type="number"> rejects the `,`
// keystroke outright, before it ever reaches onChange) or, where the field
// was already a plain text input, got stored verbatim as part of the cell's
// price string with no conversion at all.
//
// normalizePriceInput is the single place both inline price editors
// (PlanogramCell.tsx's Visual card, PlanogramTabular.tsx's table row) route
// every keystroke through before calling onUpdateCellField/handleFieldChange.
// It:
//   1. Converts a comma decimal separator to a dot (`6,50` -> `6.50`).
//   2. Strips every character that isn't a digit or a decimal point — no
//      letters, currency symbols, spaces, or anything else can ever survive
//      into stored cell data (this is the "only numeric values, never
//      strings" requirement: the result is guaranteed to be either an empty
//      string — the field cleared — or a string that Number() parses
//      cleanly, with the underlying price/cell model converting it to a
//      real number at the point it's actually stored — see
//      toStoredPriceValue below).
//   3. Collapses multiple decimal points to just the first one typed, so
//      e.g. pasting `6.5.0` (or typing a second `,`/`.` by mistake) doesn't
//      produce something Number() can't parse.
//   4. Strips leading zeros beyond a single one (`007` -> `7`), but leaves
//      a single leading `0` alone so `0.5` stays typable.
//   5. Caps the fractional part at 2 digits (`6.789` -> `6.78`) — a price
//      is currency, so anything finer than cents/groszy is never a value
//      this field should be able to hold, regardless of which decimal
//      separator was used to type it.
//
// It deliberately does NOT reject a trailing decimal point (`"6."`) or an
// empty string — those are valid INTERMEDIATE states while the user is
// still typing (e.g. about to type the digits after the separator, or
// having just cleared the field), and forcing them to resolve to a final
// number on every keystroke would make the decimal point impossible to
// type at all (it would immediately collapse back to "6").
// ---------------------------------------------------------------------------

/**
 * Sanitizes a single keystroke's worth of raw input into a clean decimal
 * string: comma converted to dot, every other invalid character stripped,
 * at most one decimal point kept, fractional part capped at 2 digits. Safe
 * to call on every onChange — always returns a string that's either empty
 * or unambiguously a (possibly incomplete) decimal number, never containing
 * letters or symbols.
 */
export function normalizePriceInput(rawValue: string): string {
    if (!rawValue) return '';

    // Comma -> dot. Do this before stripping, so `6,50` becomes `6.50`
    // rather than having its comma stripped and silently turning into `650`.
    const withDotSeparator = rawValue.replace(/,/g, '.');

    // Keep only digits and dots — removes letters, currency symbols (zł,
    // PLN, $, etc.), spaces, and any other stray character a paste could
    // introduce.
    const digitsAndDotsOnly = withDotSeparator.replace(/[^0-9.]/g, '');

    // Collapse to at most one decimal point: keep the FIRST one typed (so
    // typing "6.5.0" or a duplicate separator keeps "6.50" worth of digits
    // rather than discarding everything after the second separator).
    const firstDotIndex = digitsAndDotsOnly.indexOf('.');
    const oneDotOnly =
        firstDotIndex === -1
            ? digitsAndDotsOnly
            : digitsAndDotsOnly.slice(0, firstDotIndex + 1) +
              digitsAndDotsOnly.slice(firstDotIndex + 1).replace(/\./g, '');

    // Cap the fractional part at 2 digits — a price is currency; "6.789"
    // isn't a valid amount no matter which separator produced it. Only
    // trims when there ARE more than 2 digits after the dot, so typing
    // "6.7" or "6.78" is completely unaffected mid-entry.
    const dotIndex = oneDotOnly.indexOf('.');
    const capped =
        dotIndex === -1
            ? oneDotOnly
            : oneDotOnly.slice(0, dotIndex + 3);

    // Strip extra leading zeros ("007" -> "7"), but leave a single leading
    // zero alone so "0.5" stays typable and doesn't collapse to ".5".
    const withoutLeadingZeros = capped.replace(/^0+(?=\d)/, '');

    return withoutLeadingZeros;
}

/**
 * Converts a normalized price string into the value that should actually
 * be STORED on the cell, as a string (matching every other inline field's
 * existing `value: string` contract through onUpdateCellField/
 * handleFieldChange — this fix only needed to guarantee the CONTENT is
 * strictly numeric, never that the JS runtime type changes everywhere
 * downstream). Per GH #534's "only numeric value could be provided, not
 * any strings" requirement: the returned string is always either empty
 * (field cleared) or the canonical decimal-string form of a real,
 * non-negative, finite number — re-parsed through Number()/String() so any
 * redundant leading zeros, trailing dot-with-nothing-after, or comma that
 * slipped through normalizePriceInput are resolved to the simplest valid
 * numeric string. The one exception is a bare trailing decimal point
 * (`"6."`), kept as-is since the user is still mid-keystroke typing the
 * digits after it — collapsing it to `"6"` on every keystroke would make
 * the separator impossible to type at all.
 *
 * Rejects (falls back to '') anything that isn't a non-negative finite
 * number — defensive even though normalizePriceInput's allowlist already
 * can't produce a `-` or letters, this is the strict validation boundary
 * that decides what's actually allowed to reach stored cell data, so it
 * doesn't rely solely on the sanitizer upstream never changing.
 */
export function toStoredPriceValue(normalizedValue: string): string {
    if (normalizedValue === '') return '';
    if (normalizedValue.endsWith('.')) return normalizedValue;

    const parsed = Number(normalizedValue);
    if (Number.isNaN(parsed) || !Number.isFinite(parsed) || parsed < 0) return '';
    return String(parsed);
}

/**
 * Convenience wrapper combining both steps — what every onChange handler
 * for a Price field should call: takes the raw event value, returns what
 * to actually pass to onUpdateCellField/handleFieldChange.
 */
export function parsePriceFieldInput(rawValue: string): string {
    return toStoredPriceValue(normalizePriceInput(rawValue));
}